import React, { FC, ButtonHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { labelLarge } from '../../../typography';
import FontIcon from '../FontIcon';

export type ButtonBarVariant = 'filled' | 'outline' | 'text';

export interface ButtonBarProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonBarVariant;
  icon?: ReactNode | string;
  fullWidth?: boolean;
  compact?: boolean;
}

export const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-size: 20px;
  line-height: 1;
  
  svg, img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

export const buttonBarStyles = css<{
  $variant: ButtonBarVariant;
  $fullWidth?: boolean;
  $compact?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 100px;
  padding: ${({ $compact }) => ($compact ? '6px 12px' : '16px 24px')};
  cursor: pointer;
  border: none;
  background: transparent;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
  outline: none;
  position: relative;
  overflow: hidden;

  /* Typography */
  ${labelLarge}

  /* Premium Subtle Hover/Active State Layer */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: currentColor;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  &:hover:not(:disabled)::after {
    opacity: 0.08;
  }

  &:active:not(:disabled)::after {
    opacity: 0.12;
  }

  /* Filled Variant (Normal / Primary) */
  ${({ $variant }) =>
    $variant === 'filled' &&
    css`
      background-color: var(--sys-color--primary);
      color: var(--sys-color--on-primary);
      box-shadow: 0 1px 2px color-mix(in srgb, var(--sys-color--shadow) 5%, transparent);

      &:hover:not(:disabled) {
        box-shadow: 0 2px 4px color-mix(in srgb, var(--sys-color--shadow) 8%, transparent), 0 1px 2px color-mix(in srgb, var(--sys-color--shadow) 4%, transparent);
      }

      &:active:not(:disabled) {
        transform: scale(0.98);
      }

      &:disabled {
        background-color: var(--sys-color--outline-variant);
        color: color-mix(in srgb, var(--sys-color--on-surface) 38%, transparent);
        box-shadow: none;
      }
    `}

  /* Outline Variant (Secondary) */
  ${({ $variant }) =>
    $variant === 'outline' &&
    css`
      background-color: transparent;
      border: 1px solid var(--sys-color--outline-variant);
      color: var(--sys-color--on-surface-variant);

      &:hover:not(:disabled) {
        border-color: var(--sys-color--primary);
      }

      &:active:not(:disabled) {
        transform: scale(0.98);
      }

      &:disabled {
        border-color: color-mix(in srgb, var(--sys-color--on-surface) 12%, transparent);
        color: color-mix(in srgb, var(--sys-color--on-surface) 38%, transparent);
      }
    `}

  /* Text Variant (Tertiary) */
  ${({ $variant, $compact }) =>
    $variant === 'text' &&
    css`
      background-color: transparent;
      color: var(--sys-color--primary);
      padding: ${$compact ? '6px 12px' : '16px 16px'};

      &:active:not(:disabled) {
        transform: scale(0.98);
      }

      &:disabled {
        color: color-mix(in srgb, var(--sys-color--on-surface) 38%, transparent);
      }
    `}

  /* Shared Disabled State */
  &:disabled {
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StyledButton = styled.button<{
  $variant: ButtonBarVariant;
  $fullWidth?: boolean;
  $compact?: boolean;
}>`
  ${buttonBarStyles}
`;

export const ButtonBar: FC<ButtonBarProps> = ({
  variant = 'filled',
  icon,
  fullWidth,
  compact,
  children,
  ...props
}) => {
  const renderIcon = () => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      if (!icon.includes('.') && !icon.includes('/')) {
        return <FontIcon name={icon} size={20} />;
      }
      return <img src={icon} alt="" />;
    }
    return icon;
  };

  return (
    <StyledButton
      type={props.type || 'button'}
      $variant={variant}
      $fullWidth={fullWidth}
      $compact={compact}
      {...props}
    >
      {icon && (
        <IconWrapper>
          {renderIcon()}
        </IconWrapper>
      )}
      <span>{children}</span>
    </StyledButton>
  );
};
export default ButtonBar;
